import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  CreateProductWithArticlesRequestDTO,
  PaginatedInventoryDTO,
  PaginatedProductsViewDTO,
  ProductVersionLocationCountDTO,
  UIService,
} from '@api';
import { lastValueFrom } from 'rxjs';

interface UIState {
  inventory: PaginatedInventoryDTO | null;
  productsView: PaginatedProductsViewDTO | null;
  isLoading: boolean;
  fetchInventoryError: boolean;
  fetchProductsViewError: boolean;
  artworkCreationError: string | null;
  articleCountPerLocationByProductError: string | null;
  articleCountPerLocationByProduct: ProductVersionLocationCountDTO[] | null;
}

const initialState: UIState = {
  inventory: null,
  productsView: null,
  isLoading: false,
  fetchInventoryError: false,
  fetchProductsViewError: false,
  artworkCreationError: null,
  articleCountPerLocationByProductError: null,
  articleCountPerLocationByProduct: null,
};

export const UIStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods((store) => {
    const uiService = inject(UIService);

    return {
      async fetchInventory(page?: number, size?: number, sort?: string, query?: string, inShipment?: boolean) {
        patchState(store, () => ({
          isLoading: true,
          fetchInventoryError: false,
        }));
        try {
          const inventory: PaginatedInventoryDTO = await lastValueFrom(
            uiService.getInventory(page, size, sort, query, inShipment),
          );
          patchState(store, () => ({
            inventory,
            isLoading: false,
          }));
        } catch (error) {
          console.error(`Error fetching Inventory: ${error}`);
          patchState(store, () => ({
            isLoading: false,
            fetchInventoryError: true,
          }));
        }
      },

      async fetchProductsView(page?: number, size?: number, sort?: string, query?: string) {
        patchState(store, () => ({
          isLoading: true,
          fetchProductsViewError: false,
        }));
        try {
          const productsView = await lastValueFrom(uiService.getProductsView(page, size, sort, query));
          patchState(store, () => ({
            productsView,
            isLoading: false,
          }));
        } catch (error) {
          console.error(`Error fetching products view: ${error}`);
          patchState(store, () => ({
            isLoading: false,
            fetchProductsViewError: true,
          }));
        }
      },

      async createProductWithArticles(productWithArticles: CreateProductWithArticlesRequestDTO) {
        patchState(store, () => ({
          isLoading: true,
          error: null,
          artworkCreationError: null,
        }));
        try {
          await lastValueFrom(uiService.createProductWithArticles(productWithArticles));
          patchState(store, () => ({
            isLoading: false,
          }));
        } catch (error) {
          const message =
            typeof error === 'object' && error && 'message' in error
              ? (error as Error).message
              : 'Failed to create new artwork.';
          console.error(`Error creating new artwork: ${error}`);
          patchState(store, () => ({
            isLoading: false,
            artworkCreationError: message,
          }));
        }
      },

      async getArticleCountPerLocationByProduct(productId: string) {
        patchState(store, () => ({
          error: null,
          articleCountPerLocationByProductError: null,
        }));
        try {
          const data = await lastValueFrom(uiService.getArticleCountPerLocationByProduct(productId));
          patchState(store, () => ({
            articleCountPerLocationByProduct: data,
          }));
        } catch (error) {
          const message =
            typeof error === 'object' && error && 'message' in error
              ? (error as Error).message
              : 'Failed to fetch article count per location.';
          console.error(`Error fetching article count per location: ${error}`);
          patchState(store, () => ({
            articleCountPerLocationByProductError: message,
          }));
        }
      },
    };
  }),
);
